import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactHtmlParser from "react-html-parser";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { ModalButtonGroup, ModalSaveButton } from "../../components";
import { Password, Email } from "../Forms";
import { Auth } from "../../api";
import { setSessionData } from "../../actions/session";
import {
  setSettingsPasswordDone,
  setSettingsPasswordError,
  setSettingsPasswordSaving,
} from "../../actions/settings";

function SettingsLoginForm({ onClose, isForgotPassword = false }) {
  const [doingReset, setDoingReset] = useState(false);

  const initialValues = useMemo(
    () => ({ email: isForgotPassword || "", password: "" }),
    [isForgotPassword]
  );

  useEffect(() => {
    setDoingReset(!!isForgotPassword);
  }, [isForgotPassword]);

  const [error, setError] = useState(false);
  const [message, setMessage] = useState(false);

  const {
    settingsPasswordDone,
    settingsPasswordSaving,
    settingsPasswordError,
    settingsPasswordSuccess,
  } = useSelector((state) => state.settings);

  const handleToggleDoingReset = useCallback(
    () => setDoingReset((prev) => !prev),
    []
  );

  const handleCloseError = useCallback(() => setError(false), []);

  const validationSchema = useCallback(
    () =>
      Yup.object().shape({
        email: Yup.string()
          .email("Please enter a valid email address")
          .required("Please enter your email"),
        password: Yup.string().test({
          message: "Please enter a password",
          test: (value) => doingReset || value?.length,
        }),
      }),
    [doingReset]
  );
  const dispatch = useDispatch();

  const handleReset = useCallback(
    async (values) => {
      setError(false);
      dispatch(setSettingsPasswordSaving());
      setMessage(false);
      try {
        const res = await Auth.requestPasswordReset(values.email);
        setMessage(res.data.status);
        setError(false);
        dispatch(setSettingsPasswordDone());
      } catch (error) {
        dispatch(setSettingsPasswordError(error.message));
        setError(error.message);
      }
    },
    [dispatch]
  );

  let buttonType = useMemo(() => {
    switch (true) {
      case settingsPasswordDone:
        return "";
      case settingsPasswordError:
        return "error";
      case settingsPasswordSuccess:
        return "success";
      case settingsPasswordSaving:
        return "saving";
      default:
        return "";
    }
  }, [
    settingsPasswordDone,
    settingsPasswordSaving,
    settingsPasswordError,
    settingsPasswordSuccess,
  ]);

  const navigate = useNavigate();

  const handleLogin = useCallback(
    async ({ email, password }, { setErrors }) => {
      const domain = email.split("@")[1];
      if (domain === "afes.org.au") {
        setError(`As you are using an AFES email address, please login using the <a href="${process.env.REACT_APP_API_URL}/oauth/azure">Staff</a> button.`);
        return;
      }
      setError(false);
      setMessage(false);
      dispatch(setSettingsPasswordSaving());
      try {
        await Auth.doLogin(email, password);
        const session = await Auth.getSession();
        dispatch(setSessionData(session));
        navigate(`/programs`);
        onClose();
      } catch (error) {
        setError(error?.message || "");
        dispatch(
          setSettingsPasswordError(
            "An error has occurred, please review your credentials and try again"
          )
        );
      }
    },
    [navigate, dispatch, onClose]
  );

  return (
    <Formik
      onSubmit={doingReset ? handleReset : handleLogin}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ handleSubmit }) => {
        return (
          <form
            id="loginForm"
            className="form login__content"
            onSubmit={handleSubmit}
          >
            <div className="form__wrapper">
              {doingReset ? <h2>Forgotten Password</h2> : <h2>Welcome Back</h2>}

              {doingReset && (
                <div className="flex-row form__row">
                  Please enter your email and we’ll send you a link so you can
                  reset your password.
                </div>
              )}
              <div className="flex-row form__row">
                <Email
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  className="flex-row__stretch "
                  style={{ width: "-webkit-fill-available" }}
                />
              </div>

              {!doingReset && (
                <div className="flex-row form__row">
                  <Password
                    label="Password"
                    name="password"
                    autoComplete="current-password"
                    className="flex-row__stretch "
                    style={{ width: "-webkit-fill-available" }}
                  />
                </div>
              )}

              <div className="flex-row form__finalise-changes">
                <ModalButtonGroup>
                  <>
                    <ModalSaveButton
                      variant={buttonType}
                      onClick={handleSubmit}
                      onTryAgain={(e) => {
                        e.preventDefault();
                        handleSubmit();
                      }}
                    >
                      {doingReset ? "Send Password Reset" : "Login"}
                    </ModalSaveButton>
                    <div className="form_row form_row--last">
                      <div className="form_note">
                        <a
                          href="#"
                          onClick={handleToggleDoingReset}
                          className="text_link forgot-switch"
                        >
                          {doingReset
                            ? "Return to Login Screen"
                            : "Forgotten Password"}
                        </a>
                      </div>
                    </div>
                  </>
                </ModalButtonGroup>
              </div>

              {error && (
                <div className="remodal__validation validation">
                  <div className="alert-box alert ajax-submit-alert">
                    <span className="validate-message">
                      {ReactHtmlParser(error)}
                    </span>
                    <a href="" onClick={handleCloseError} className="close">
                      ×
                    </a>
                  </div>
                </div>
              )}
              {message && (
                <div className="remodal__validation validation">
                  <div className="alert-box success ajax-submit-alert">
                    <span className="validate-message">
                      {ReactHtmlParser(message)}
                    </span>
                    <a href="" onClick={handleCloseError} className="close">
                      ×
                    </a>
                  </div>
                </div>
              )}
            </div>
          </form>
        );
      }}
    </Formik>
  );
}

export default SettingsLoginForm;
